import { DeleteIcon, LoadingIcon } from '@assets/icons';
import { CopyIconButton, IconButton } from '@components/button';
import { useModal } from '@components/modal';
import { DateDisplay, Typography } from '@components/typography';
import { APIKey, useGetAPIKeysQuery } from '@generated/graphql';
import { Box, Divider } from '@mui/material';
import { CreateApiKeyModal } from './CreateApiKeyModal';
import { DeleteApiKeyModal } from './DeleteApiKeyModal';
import { KeysContainer } from './KeysContainer';

const APIKeyRow = ({ apiKey, refetch }: { apiKey: APIKey; refetch: () => void }) => {
  const { showModal: showDeleteModal } = useModal({
    component: DeleteApiKeyModal,
    componentProps: { name: apiKey.name, onClose: refetch },
  });

  return (
    <>
      <Typography variant="body1" sx={{ color: 'text.paper' }}>
        {apiKey.name}
      </Typography>
      <DateDisplay date={apiKey.createdAt} sx={{ color: 'text.paper' }} />
      <DateDisplay date={apiKey.expiresAt} sx={{ color: 'text.paper' }} nullOverride="Never" />
      <Typography variant="body2" sx={{ height: '28px', lineHeight: '28px', color: 'text.paper' }}>
        {apiKey.scope}{' '}
        {apiKey.scope === 'organization' || apiKey.scope === 'workspace' ? (
          <CopyIconButton
            value={apiKey.scope === 'organization' ? apiKey.organizationId : apiKey.workspaceId}
            showValue
          />
        ) : null}
      </Typography>
      <IconButton Icon={DeleteIcon} onClick={() => showDeleteModal()} />
    </>
  );
};

export const APIKeys = () => {
  const { data, loading, error, refetch } = useGetAPIKeysQuery();
  const keys = data?.getAPIKeys || [];

  const { showModal } = useModal({
    component: CreateApiKeyModal,
    componentProps: { onClose: refetch },
    modalProps: { title: 'Create API Key' },
  });

  const onCreate = () => {
    showModal();
  };

  return (
    <KeysContainer
      title="API Keys"
      onCreate={onCreate}
      count={loading ? null : keys.length}
      actionButtonLabel="Create API key"
    >
      {loading ? (
        <LoadingIcon />
      ) : error ? (
        <Typography variant="caption2">Error loading API keys</Typography>
      ) : keys.length === 0 ? (
        <Typography variant="caption2" sx={{ display: 'inline-block' }}>
          No API keys found
        </Typography>
      ) : (
        <Box
          gap={2}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto auto auto auto min-content',
            gridAutoRows: 'auto',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              px: 2,
              display: 'grid',
              gridColumn: 'span 5',
              gridTemplateColumns: 'subgrid',
              alignItems: 'center',
            }}
          >
            <Typography variant="caption1">Name</Typography>
            <Typography variant="caption1">Created</Typography>
            <Typography variant="caption1">Expires</Typography>
            <Typography variant="caption1">Scope</Typography>
          </Box>
          <Divider sx={{ gridColumn: 'span 5' }} />
          {keys.map((apiKey) => (
            <Box
              key={apiKey.name}
              sx={{
                px: 2,
                display: 'grid',
                gridColumn: 'span 5',
                gridTemplateColumns: 'subgrid',
                alignItems: 'center',
                '&:hover': { backgroundColor: 'backgroundHover.paper' },
              }}
            >
              <APIKeyRow apiKey={apiKey} refetch={refetch} />
            </Box>
          ))}
        </Box>
      )}
    </KeysContainer>
  );
};
